import {Amplify} from 'aws-amplify'
import {
  deleteUser,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signInWithRedirect,
  SignInWithRedirectInput,
  signOut,
  updateUserAttributes,
  UpdateUserAttributesOutput
} from 'aws-amplify/auth'
import {cognitoUserPoolsTokenProvider} from 'aws-amplify/auth/cognito'
import {CookieStorage} from 'aws-amplify/utils'
import {anyPass, isNil, omit, reject} from 'ramda'

import {getBrandCodeFromUrlOrCookie} from '../../_web/brand/utils'
import {
  getNormalizedEnvironment,
  NormalizedEnvironment
} from '../../_web/utils/environment'
import {
  safelyGetStorageItem,
  safelyRemoveStorageItem,
  safelySetStorageItem
} from '../../_web/utils/persistence'
import AnalyticsUtils from '../../analytics/utils'
import {UserTier} from '../../offer/business/privateDeals'
import {logger} from '../../utils/logging/logger'
import {getAmplifyConfig} from '../amplifyConfig/getConfig'
import {USER_INFO_API_ENDPOINT} from '../constants'
import {
  AuthenticationFlows,
  AuthenticationMethod,
  SourceComponentType,
  UserInfoResponse
} from '../types/Auth'
import {User, UserAttributes, UserPoolId, UserPoolName} from '../types/Cognito'

export {Hub} from 'aws-amplify/utils'

export const AUTHENTICATION_CONTEXT_STORAGE_KEY = 'authenticationContext'
export const AUTHENTICATION_SUCCESS_STORAGE_KEY = 'authJustSucceeded'

export type PreAuthContext = {
  sourceComponent: SourceComponentType
  provider?: SignInWithRedirectInput['provider']
  authenticationMethod?: AuthenticationMethod
  /** Required so an attempt cannot be persisted without stating its flow. */
  flow: AuthenticationFlows | null
  analyticsData?: Record<string, unknown>
  endedAt?: number
}

const persistPreAuthContextForPostRedirect = (context: PreAuthContext) => {
  if (!context.sourceComponent) return

  safelySetStorageItem(
    sessionStorage,
    AUTHENTICATION_CONTEXT_STORAGE_KEY,
    JSON.stringify(context),
    'persistPreAuthContextForPostRedirect failed'
  )
}

/**
 * Stamps the attempt finished. Kept rather than deleted, because marketing
 * notifications reads the record a page load later on the destination page.
 */
export const endPreAuthAttempt = () => {
  const context = getPreAuthContext()

  if (!context || context.endedAt) return

  safelySetStorageItem(
    sessionStorage,
    AUTHENTICATION_CONTEXT_STORAGE_KEY,
    JSON.stringify({...context, endedAt: Date.now()}),
    'endPreAuthAttempt failed'
  )
}

/** Where the current attempt started, surviving a full page redirect. */
export const getPreAuthContext = (): PreAuthContext | undefined => {
  const raw = safelyGetStorageItem(
    sessionStorage,
    AUTHENTICATION_CONTEXT_STORAGE_KEY,
    'getPreAuthContext failed'
  ) as string | undefined

  if (!raw) return undefined

  try {
    return JSON.parse(raw)
  } catch {
    return undefined
  }
}

export const getPreAuthAnalyticsData = ():
  | Record<string, unknown>
  | undefined => getPreAuthContext()?.analyticsData

/**
 * Persist the authentication success flag before redirecting
 */
export const persistAuthJustSucceeded = () => {
  safelySetStorageItem(
    sessionStorage,
    AUTHENTICATION_SUCCESS_STORAGE_KEY,
    '1',
    'persistAuthJustSucceeded failed'
  )
}

/**
 * Configure Amplify
 */
export const configureAmplify = (
  brand: string,
  environment: NormalizedEnvironment
) => {
  const amplifyConfig = getAmplifyConfig(location.origin, brand, environment)
  Amplify.configure(amplifyConfig)

  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage(amplifyConfig.cookieStorage as CookieStorage)
  )

  return amplifyConfig
}

const OAUTH_INFLIGHT_STORAGE_KEYS = ['inflightOAuth', 'oauthPKCE', 'oauthState']

/**
 * Amplify only clears its in-flight OAuth flag on a page load, which never
 * happens when the app WebView cancels the redirect. Left behind, the flag
 * blocks every later token read, and Amplify has no API to clear it.
 * The keys copy the private `DefaultOAuthStore.clearOAuthInflightData` in
 * `@aws-amplify/auth`, so recheck them when upgrading Amplify.
 */
export const clearStaleOAuthInflightState = () => {
  if (new URLSearchParams(location.search).has('code')) return

  const {aws_user_pools_web_client_id} = getAmplifyConfig(
    location.origin,
    getBrandCodeFromUrlOrCookie(),
    getNormalizedEnvironment()
  )

  OAUTH_INFLIGHT_STORAGE_KEYS.forEach(key =>
    safelyRemoveStorageItem(
      'localStorage',
      `CognitoIdentityServiceProvider.${aws_user_pools_web_client_id}.${key}`,
      'clearStaleOAuthInflightState failed'
    )
  )
}

/**
 * Amplify SignOut
 */
export const signOutService = async () => {
  await signOut()
  AnalyticsUtils.resetAnalytics()
}

/**
 * Amplify Federated Social Login
 * @param provider - Provider to use for login
 * @param context - Where the attempt started, persisted so it survives the redirect
 */
export const socialLoginService = async (
  provider: SignInWithRedirectInput['provider'],
  context: Omit<PreAuthContext, 'provider'>
) => {
  const {sourceComponent} = context

  clearStaleOAuthInflightState()
  persistPreAuthContextForPostRedirect({...context, provider})

  try {
    await signInWithRedirect({
      provider,
      customState: `sourceComponent=${sourceComponent}&provider=${provider}`
    })
  } catch (error) {
    console.log(error)
  }
}

interface UserWithPoolId {
  user: User | null
  userPoolId: UserPoolId | ''
}

/**
 * Amplify Get current user
 * @returns UserWithPoolId
 */
export const getCurrentUserService = async (): Promise<UserWithPoolId> => {
  const brand = getBrandCodeFromUrlOrCookie()
  const amplifyConfig = getAmplifyConfig(
    location.origin,
    brand,
    getNormalizedEnvironment()
  )
  const {aws_user_pools_id} = amplifyConfig

  const nullableUser: UserWithPoolId = {user: null, userPoolId: ''}

  try {
    const {idToken} = await getUserSessionService()

    if (idToken) {
      const currentUser = await getCurrentUser()
      const attributes = await getUserAttributesService()

      const {createdAt, updatedAt} = idToken.payload as {
        createdAt: string
        updatedAt: string
      }

      const user: User = {
        ...currentUser,
        id: attributes.sub,
        attributes,
        createdAt,
        updatedAt
      }

      return {user, userPoolId: aws_user_pools_id as UserPoolId}
    }
  } catch (error) {
    return nullableUser
  }

  return nullableUser
}

/**
 * Wrapper to get user info from server (Extract from vio_wl_context cookie)
 * @returns UserInfoResponse['user'] | null
 */
export const getUserInfoFromServer = async (): Promise<
  UserInfoResponse['user'] | null
> => {
  try {
    const response = await fetch(USER_INFO_API_ENDPOINT)

    if (!response.ok) {
      throw new Error(response.statusText)
    }

    const data: UserInfoResponse = await response.json()
    return data?.user
  } catch (err) {
    logger.info('Failed to fetch user info:', err)
    return null
  }
}

/**
 * Amplify fetchAuthSession() wrapper method
 * @param forceRefresh - Force refresh the session
 */
export const getUserSessionService = async (forceRefresh = false) => {
  const brand = getBrandCodeFromUrlOrCookie()
  const environment = getNormalizedEnvironment()
  const amplifyConfig = getAmplifyConfig(location.origin, brand, environment)
  const {aws_user_pools_web_client_id} = amplifyConfig
  const lastAuthUserKey = `CognitoIdentityServiceProvider.${aws_user_pools_web_client_id}.LastAuthUser`

  if (!document.cookie.includes(lastAuthUserKey)) {
    return {idToken: undefined, accessToken: undefined}
  }

  const {tokens} = (await fetchAuthSession({forceRefresh})) || {}

  return {
    idToken: tokens?.idToken,
    accessToken: tokens?.accessToken
  }
}

/**
 * Amplify fetchAuthSession() wrapper method
 */
export const getUserAttributesService = async (): Promise<UserAttributes> => {
  const attributes = await fetchUserAttributes()

  const {email_verified, phone_number_verified} = attributes || {}
  const isEmailVerified = email_verified === 'true'
  const isPhoneNumberVerified = phone_number_verified === 'true'

  return {
    ...attributes,
    ...(email_verified && {email_verified: isEmailVerified}),
    ...(phone_number_verified && {phone_number_verified: isPhoneNumberVerified})
  } as UserAttributes
}

/**
 * update user attribute of the current authenticated user
 * @param attributes - attributes to update
 */
export const updateUserAttributesService = async (
  attributes: UserAttributes
): Promise<UpdateUserAttributesOutput> => {
  const attributesToUpdate = reject(anyPass([isNil]))(
    omit(['email', 'phone_number'], attributes)
  )

  const response = await updateUserAttributes({
    /* @ts-expect-error: we're strongly typing UserAttributes with the values we're returning from Cognito */
    userAttributes: attributesToUpdate
  })

  return response
}

const poolMap = {
  [UserPoolId.Dev]: UserPoolName.Dev,
  [UserPoolId.EtripProd]: UserPoolName.EtripProd,
  [UserPoolId.EtripStg]: UserPoolName.EtripStg,
  [UserPoolId.FindhotelProd]: UserPoolName.FindhotelProd,
  [UserPoolId.FindhotelStg]: UserPoolName.FindhotelStg,
  [UserPoolId.KiwiProd]: UserPoolName.KiwiProd,
  [UserPoolId.KiwiStg]: UserPoolName.KiwiStg
}

/**
 * Gets the user pool name from the pool ID
 * @param userPoolId - The ID of the user pool
 * @returns The corresponding user pool name or null
 */
export const getUserPoolName = (
  userPoolId: UserPoolId | null
): UserPoolName | null => userPoolId && poolMap[userPoolId]

/**
 * Upgrade current authenticated user to specific tier
 * this function does not overwrite previous attributes nor tiers
 * @param tier - the new tier ex: UserTier.Plus
 */
export const upgradeUserTier = async (
  tier: UserTier
): Promise<UserAttributes['custom:app_data']> => {
  const attributes = await getUserAttributesService()

  const appData = JSON.parse(attributes['custom:app_data'] || '{}')

  const previousTiers = appData?.tier || []

  const newAppData = JSON.stringify({
    ...appData,
    tier: [...new Set([...previousTiers, tier])]
  })

  await updateUserAttributesService({'custom:app_data': newAppData})
  return newAppData
}

export const deleteUserService = () => deleteUser()

/**
 * SignIn/Login
 * @param email - User's email address
 * @param brand - Brand identifier
 * @param languageCode - Language code for localization
 * @param apiUrl - API endpoint URL
 * @param sourceComponent - Source component type
 * @param flow - Authentication flow the attempt started from
 */
export const startLoginSignUp = async (
  email: string,
  brand: string,
  languageCode: string,
  apiUrl: string,
  sourceComponent: SourceComponentType,
  flow: AuthenticationFlows | null
) => {
  const environment = getNormalizedEnvironment()
  const {aws_user_pools_web_client_id} = configureAmplify(brand, environment)

  clearStaleOAuthInflightState()
  persistPreAuthContextForPostRedirect({
    sourceComponent,
    authenticationMethod: AuthenticationMethod.Email,
    flow
  })

  try {
    const response = await fetch(`${apiUrl}auth/send-email-one-time-password`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        brand,
        languageCode,
        email,
        clientId: aws_user_pools_web_client_id
      })
    })

    if (!response.ok) {
      throw new Error(response.statusText)
    }

    const data = await response.json()
    return data?.response
  } catch (error) {
    logger.error('startLoginSignUp:', error)
    throw error
  }
}

/**
 * Verifies the one-time password for user authentication
 * @param email - User's email address
 * @param otp - One-time password code
 * @param session - Authentication session
 * @param apiUrl - API endpoint URL
 */
export const verifyOneTimePassword = async (
  email: string,
  otp: string,
  session: string,
  apiUrl: string
) => {
  const brand = getBrandCodeFromUrlOrCookie()
  const environment = getNormalizedEnvironment()
  const amplifyConfig = getAmplifyConfig(location.origin, brand, environment)
  const {aws_user_pools_web_client_id} = amplifyConfig

  const response = await fetch(`${apiUrl}auth/verify-email-one-time-password`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({
      email,
      otp,
      session,
      clientId: aws_user_pools_web_client_id
    })
  })

  if (!response.ok) {
    // Callers branch on status and recover the session from the body, so both
    // have to survive on the error.
    throw Object.assign(new Error(response.statusText), {
      response: {
        status: response.status,
        data: await response.json().catch(() => undefined)
      }
    })
  }

  const data = await response.json()

  const tokens = {
    idToken: data.response.idToken,
    accessToken: data.response.accessToken,
    refreshToken: data.response.refreshToken,
    username: email,
    clockDrift: 5000
  }

  await cognitoUserPoolsTokenProvider.authTokenStore.storeTokens(tokens)
}
